import React, { useEffect, useState } from "react";
import { useRouteMatch } from "react-router-dom";
import { ButtonRenderProps, ContentSection } from "sprint-common-web";

import { useCachedAssetsApi } from "../../../../common/api/master-api/assets/useCachedAssetsApi";
import { AssetType } from "../../../../common/types/AssetType";
import { ConformingCloudAsset } from "../../../api/GetConformingCloudAssetsResponse";
import { ConformingOnPremAsset } from "../../../api/GetConformingOnPremAssetsResponse";
import { useConformingCloudAssets } from "../../cloud-asset/common/hooks/useConformingCloudAssets";
import { getConformingAssetDetailsHref } from "../utils/getConformingAssetDetailsHref";
import { ConformingAssetListingItem, mapToConformingOnPremAssetListingItem } from "./ConformingAssetItem";
import { ConformingAssetList } from "./ConformingAssetList";
import { createConformingAssetsSectionColumns } from "./createConformingAssetsSectionColumns";
import {
    DeprecateAssetGroupDialogButton,
    DeprecateAssetGroupDialogButtonProps
} from "./deprecate/DeprecateAssetGroupDialogButton";

interface ConformingAssetsSectionProps {
    canBeDeprecated: boolean;
    contentVersionIds: string[];
    setStatus?: (status: number) => void;
    assetType: AssetType;
}

export const ConformingAssetsSection: React.FC<ConformingAssetsSectionProps> = ({
    canBeDeprecated,
    contentVersionIds,
    assetType
}: ConformingAssetsSectionProps) => {
    const [onPremConformingAssets, setOnPremConformingAssets] = useState<ConformingOnPremAsset[]>([]);
    const [isLoadingOnPrem, setIsLoadingOnPrem] = useState<boolean>(true);
    const { params } = useRouteMatch<{ assetId: string; assetType: string }>();
    const isCloud = isCloudAsset(assetType);

    const { getConformingOnPremAssets } = useCachedAssetsApi();
    const { data: cloudConformingAssets, isLoading: isLoadingCloud } = useConformingCloudAssets(
        isCloud ? params.assetId : undefined
    );

    useEffect(() => {
        if (isCloud) return;

        callForOnPremConformingAssets();
    }, [isCloud]);

    const columns = createConformingAssetsSectionColumns({
        getHref: (item) => getConformingAssetDetailsHref(item.id, isCloud, params.assetType),
        getTooltipText: (item) => item.path,
        isAnnotated: (item) => item.id === params.assetId && "current",
        isDisabled: (item) => item.id === params.assetId
    });

    const callForOnPremConformingAssets = async () => {
        setIsLoadingOnPrem(true);

        const result = await getConformingOnPremAssets({ assetId: params.assetId });
        setOnPremConformingAssets(result.assets);

        setIsLoadingOnPrem(false);
    };

    const isLoading = isCloud ? isLoadingCloud : isLoadingOnPrem;

    const conformingAssets = getSortedConformingAssetItems(
        isCloud ? cloudConformingAssets ?? [] : onPremConformingAssets
    );

    const deprecateButton: ButtonRenderProps<DeprecateAssetGroupDialogButtonProps> = {
        render: DeprecateAssetGroupDialogButton,
        disabled: !canBeDeprecated,
        isLoading,
        isCloud,
        conformingAssets,
        contentVersionIds
    };

    return (
        <ContentSection
            header="Conforming Assets"
            isLoading={isLoading}
            buttons={[deprecateButton]}
            testId="conforming-assets-content-section"
        >
            <ConformingAssetList items={conformingAssets} columns={columns} isLoading={isLoading} />
        </ContentSection>
    );
};

const getSortedConformingAssetItems = (
    assets: Array<ConformingCloudAsset | ConformingOnPremAsset>
): ConformingAssetListingItem[] => {
    return assets
        .map(mapToConformingOnPremAssetListingItem)
        .sort((prevAsset, nextAsset) => (prevAsset.recordType >= nextAsset.recordType ? 1 : -1));
};

const isCloudAsset = (assetType: AssetType) => assetType === AssetType.Cloud;
